// Distillation loader — the base.html noise→solve choreography as a
// React component. Renders `phrase` as mono tokens and re-animates
// whenever `phrase` / `triggerKey` changes. Used for headline numbers,
// section-title swaps, and the key-flash banner.

import React from "react";

const LOWER = "abcdefghijklmnopqrstuvwxyz";
const UPPER = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

const isStable = (c: string): boolean =>
  /\s/.test(c) || /[.,:;!?()/\-_'"—&$€%]/.test(c);

function randLike(c: string): string {
  if (/[A-Z]/.test(c)) return UPPER[(Math.random() * 26) | 0];
  if (/[a-z]/.test(c)) return LOWER[(Math.random() * 26) | 0];
  if (/[0-9]/.test(c)) return String((Math.random() * 10) | 0);
  return c;
}

function noiseGlyphs(real: string): string {
  let out = "";
  for (let i = 0; i < real.length; i++) {
    const c = real[i];
    out += isStable(c) ? c : randLike(c);
  }
  return out;
}

export function Distill({
  phrase,
  className = "",
  triggerKey,
  steps = 7,
  frameMs = 28,
}: {
  phrase: string;
  className?: string;
  triggerKey?: string | number;
  steps?: number;
  frameMs?: number;
}) {
  const ref = React.useRef<HTMLSpanElement>(null);
  const key = triggerKey !== undefined ? `${triggerKey}::${phrase}` : phrase;
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.classList.add("cond");
    el.textContent = "";
    const toks = String(phrase)
      .split(/(\s+)/)
      .filter((t) => t.length > 0);
    const nodes: HTMLSpanElement[] = [];
    toks.forEach((t) => {
      if (/^\s+$/.test(t)) {
        el.appendChild(document.createTextNode(t));
        return;
      }
      const span = document.createElement("span");
      span.className = "cd-tok solved";
      span.textContent = t;
      span.dataset.real = t;
      el.appendChild(span);
      nodes.push(span);
    });
    let stopped = false;
    const timers: number[] = [];
    const wait = (ms: number, fn: () => void): void => {
      const t = window.setTimeout(() => {
        if (!stopped) fn();
      }, ms);
      timers.push(t);
    };
    nodes.forEach((n) => {
      n.className = "cd-tok noise";
      n.textContent = noiseGlyphs(n.dataset.real || "");
    });
    const order = [...nodes.keys()].sort(() => Math.random() - 0.5);
    const waves = 3;
    order.forEach((idx, k) => {
      const wave = (k / Math.max(1, order.length / waves)) | 0;
      const delay = wave * 100 + Math.random() * 50;
      wait(delay, () => {
        nodes[idx].className = "cd-tok solving";
        const real = nodes[idx].dataset.real || "";
        const len = real.length;
        const lockAt = new Array<number>(len);
        for (let i = 0; i < len; i++) {
          lockAt[i] = ((0.25 + 0.75 * Math.random()) * steps) | 0;
        }
        for (let s = 0; s <= steps; s++) {
          wait(s * frameMs, () => {
            let out = "";
            for (let i = 0; i < len; i++) {
              const c = real[i];
              if (s >= lockAt[i] || isStable(c)) out += c;
              else out += randLike(c);
            }
            nodes[idx].textContent = out;
          });
        }
        wait(steps * frameMs + 40, () => {
          nodes[idx].className = "cd-tok solved";
          nodes[idx].textContent = nodes[idx].dataset.real || "";
        });
      });
    });
    return () => {
      stopped = true;
      timers.forEach((t) => clearTimeout(t));
    };
  }, [key, steps, frameMs, phrase]);
  return <span ref={ref} className={`cond ${className}`} />;
}
